import { createReducer, on } from '@ngrx/store'
import * as PerfectScrollbarActions from './perfect-scrollbar.actions'
import { StoreKeysEnum } from '../../../../store/store-keys'
import cloneDeep from 'lodash/cloneDeep'
import { assign, pick } from 'lodash'

export const initialState: PerfectScrollbarState = {
    fetchRunning: false,
    flushCache: false,
    globalStatePath: undefined,
    targetKey: undefined,
    isVisible: false,
    initialLoad: false,
    isSearchComponent: false,
    refetchOnClearSearch: false,
    loadState: false,
    params: {},
    reducerKey: undefined,
    resultCache: [],
    psSelector: '',
    totalCount: 0,
    cachedData: {
        list: [],
        scrollTop: undefined,
        totalCount: undefined,
        offset: undefined,
    }
}

export const perfectScrollbarKey = StoreKeysEnum.PerfectScrollbarComponent

export const perfectScrollbarReducer = createReducer(
    {},
    on(PerfectScrollbarActions.init, (state: any, props): Record<string, PerfectScrollbarState> => {
        // The spread operator (...) is used to create a new state object at a new memory address. This ensures immutability of the state.
        // The [props.key] is a computed property name that allows the key of the new state object to be set dynamically based on the value of props.key.
        // The object { ...initialState, ...props, rootElement: element } is a new object that is being added to the state. It is created by merging props, initialState, and a new property rootElement.
        // The spread operator (...) is used again to ensure that the original props and initialState objects are not mutated, preserving immutability.
        const cachedData = state[props.key] ? cloneDeep(state[props.key]?.cachedData) : initialState.cachedData
        return { ...state, [props.key]: { ...initialState, ...props, globalStatePath: props.globalStatePath ?? props.key, cachedData, refetchOnClearSearch: props.initialLoad === true }}
    }),
    on(PerfectScrollbarActions.setSearchText, (state: any, props) => {
        return { ...state, [props.key]: { ...state[props.key], searchText: props.searchText, loadState: true}}
    }),
    on(PerfectScrollbarActions.clearSearchTextToReloadFilter, (state: any, props) => {
        return { ...state, [props.key]: { ...state[props.key], searchText: undefined, loadState: true}}
    }),
    on(PerfectScrollbarActions.fetchOriginData, (state: any, props) => {
        return { ...state, [props.key]: { ...state[props.key], fetchRunning: true}}
    }),
    on(PerfectScrollbarActions.setLoadState, (state: any, props) => {
        return { ...state, [props.key]: { ...state[props.key], loadState: props.loadState}}
    }),
    on(PerfectScrollbarActions.setFlushCache, (state: any, props) => {
        return { ...state, [props.key]: { ...state[props.key], flushCache: props.flushCache}}
    }),
    on(PerfectScrollbarActions.setTotalCount, (state: any, props) => {
        return { ...state, [props.key]: { ...state[props.key], totalCount: props.count}}
    }),
    on(PerfectScrollbarActions.setResultCache, (state: any, props) => {
        return { ...state, [props.key]: { ...state[props.key], resultCache: props.items}}
    }),
    on(PerfectScrollbarActions.setCachedData, (state: any, props) => {
        const updatedCachedData = cloneDeep(state[props.key].cachedData)
        updatedCachedData.list = props.items
        updatedCachedData.offset = props.offset
        return { ...state, [props.key]: { ...state[props.key], cachedData: updatedCachedData}}
    }),
    on(PerfectScrollbarActions.setCachedScroll, (state: any, { key, scrollTop }) => {
        const updatedCachedData = {...state[key].cachedData}
        updatedCachedData.scrollTop = scrollTop
        return { ...state, [key]: { ...state[key], cachedData: updatedCachedData}}
    }),
    //
    on(PerfectScrollbarActions.updateCachedContractData, (state: any, props) => {
        const updatedCachedData = cloneDeep(state[props.key]?.cachedData)

        if (!updatedCachedData) {
            return state
        }

        const updatedContractIndex = state[props.key]?.cachedData?.list.findIndex(item => item.id === props.contract.id)

        // No contract to update found, reset cache to update new added contracts
        if (updatedContractIndex === -1) {
            return { ...state, [props.key]: { ...state[props.key], cachedData: cloneDeep(initialState.cachedData)}}
        }
        const pickedObj2 = pick(props.contract, Object.keys(state[props.key]?.cachedData?.list[updatedContractIndex]))
        const updatedContract = assign({}, cloneDeep(state[props.key]?.cachedData?.list[updatedContractIndex]), pickedObj2)
        updatedCachedData.list[updatedContractIndex] = updatedContract

        return { ...state, [props.key]: { ...state[props.key], cachedData: updatedCachedData}}
    }),
    on(PerfectScrollbarActions.resetContractCache, (state: any, props) => {
        return { ...state, [props.key]: { ...state[props.key], cachedData: cloneDeep(initialState.cachedData)}}
    }),
    on(PerfectScrollbarActions.setLoadState, (state: any, props) => {
        return { ...state, [props.key]: { ...state[props.key], loadState: props.loadState}}
    }),
    on(PerfectScrollbarActions.postLoadAction, (state: any, props) => {
        return { ...state, [props.key]: { ...state[props.key], loadState: false, initialLoad: false, totalCount: props.data.itemCount, params: {...state[props.key].params, ...props.data.params}, fetchRunning: false }}
    }),
    on(PerfectScrollbarActions.reset, (state: any, { key }) => {
        const cachedData = cloneDeep(state[key]?.cachedData)
        if (cachedData) {
            cachedData.totalCount = state[key]?.totalCount
        }
        return { ...state, [key]: { ...initialState, cachedData: cachedData }}
    }),
    on(PerfectScrollbarActions.resetSearch, (state: Record<string, PerfectScrollbarState>, props) => {
        return { ...state, [props.key]: { ...state[props.key], searchText: '', totalCount: 0, initialLoad: props.reloadOnOpen}}
    }),
    on(PerfectScrollbarActions.resetCache, (state: Record<string, PerfectScrollbarState>) => {
        const updatedState = cloneDeep(state)
        Object.values(updatedState).forEach(item => {
            item.cachedData = cloneDeep(initialState.cachedData)
        })
        return updatedState
    }),
    //
    on(PerfectScrollbarActions.visibleChange, (state: Record<string, PerfectScrollbarState>, props) => {
        return { ...state, [props.key]: { ...state[props.key], isVisible: props.isVisible}}
    }),
)

// Interfaces
export interface PerfectScrollbarState {
    fetchRunning: boolean;
    flushCache: boolean;
    globalStatePath: string;
    targetKey: string;
    isVisible: boolean;
    initialLoad: boolean;
    isSearchComponent: boolean;
    refetchOnClearSearch: boolean;
    loadState: boolean;
    params: {};
    reducerKey: string;
    resultCache: any[];
    psSelector: string;
    totalCount: number;
    cachedData: IPerfectScrollbarCachedData;
}

export interface IPerfectScrollbarCachedData {
    list: any[];
    scrollTop: number;
    totalCount: number;
    offset: number;
}
//
